/**
 * Katalys (RevOffers) Tracking Utility
 * Handles initialization and conversion tracking for the Katalys affiliate platform.
 * Note: Only runs in production/live environment (skipped when testMode is true)
 */

import { config } from '../config/env'
import { logger } from './logger'

/**
 * Initialize the Katalys RevOffers tracking script.
 * Injects the async loader snippet — skipped in test/staging mode.
 */
export const initializeKatalys = () => {
  if (config.testMode) {
    logger.debug('ℹ️ Katalys skipped: Test/Staging mode detected')
    return
  }

  if (window._revoffers_track && window._revoffers_track._loaded) {
    logger.debug('ℹ️ Katalys already initialized')
    return
  }

  logger.debug('📊 Initializing Katalys RevOffers tracking')

  try {
    // Standard RevOffers async loader snippet
    ;(function(w, d, e, u, v, n, t, s) {
      if (w[u]) return
      n = w[u] = []
      n.push = function() { Array.prototype.push.call(w[u], arguments) }
      t = d.createElement(e)
      t.async = true
      t.src = v
      s = d.getElementsByTagName(e)[0]
      s.parentNode.insertBefore(t, s)
    })(window, document, 'script', '_revoffers_track', 'https://db.revoffers.com/js/KA-E4I5VTQSID.js')

    logger.debug('✅ Katalys RevOffers tracking initialized')
  } catch (error) {
    logger.error('❌ Error initializing Katalys:', error)
  }
}

/**
 * Fire a Katalys conversion event on the payment success / thank-you page.
 * Maps funnelData fields to the RevOffers ecommerce conversion schema.
 * All price/discount values are read directly from funnelData (already stored in
 * localStorage by ProductSelectionSection before payment is captured):
 *   - total_prescription_price  → final charged amount (discounted total)
 *   - selected_product_price    → original price (fallback when no promo)
 *   - savings_amount            → discount value applied
 *   - promo_code                → coupon code used
 *
 * Should be called only once per order (guarded by the caller's localStorage check).
 * @param {Object} funnelData - The funnel context data
 */
export const trackKatalysConversion = (funnelData) => {
  if (!window._revoffers_track) {
    logger.warn('⚠️ Katalys not initialized. Conversion not tracked.')
    return
  }

  const orderId    = funnelData.case_id || ''
  const email      = funnelData.email   || ''
  // total_prescription_price is the final discounted total stored by ProductSelectionSection
  const saleAmount = funnelData.total_prescription_price || funnelData.selected_product_price || 0
  const promoCode  = funnelData.promo_code || ''
  const savings    = parseFloat(funnelData.savings_amount) || 0

  // Build a human-readable line item title: e.g. "Semaglutide Injection - 3-Month Plan"
  const planLabel = {
    monthly:    '1-Month Plan',
    quarterly:  '3-Month Plan',
    sixMonth:   '6-Month Plan',
    annual:     '12-Month Plan',
  }[funnelData.selected_plan] || funnelData.selected_plan || ''
  const productName = funnelData.selected_product_name || funnelData.chosen_medicine_name || ''
  const lineItemTitle = planLabel ? `${productName} - ${planLabel}` : productName

  const conversionPayload = {
    action:             'convert',
    action_type:        'online-sale',
    order_id:           orderId,
    sale_amount:        saleAmount,
    subtotal_amount:    saleAmount,
    email_address:      email,
    order_status:       'pending',
    line_item_1_title:  lineItemTitle,
    line_item_1_sku:    funnelData.selected_product_id || '',
    line_item_1_price:  saleAmount,
  }

  if (promoCode && savings > 0) {
    conversionPayload.discount_1_code   = promoCode
    conversionPayload.discount_1_amount = savings
  }

  logger.debug('📊 Katalys Conversion payload:', conversionPayload)

  try {
    window._revoffers_track.push(conversionPayload)
    logger.debug('✅ Katalys conversion tracked successfully')
  } catch (error) {
    logger.error('❌ Error tracking Katalys conversion:', error)
  }
}

/**
 * Fire a Katalys conversion event for an upsell purchase (e.g. Sermorelin, Zofran).
 * Unlike trackKatalysConversion, this accepts a plain input object so callers can
 * pass upsell-specific values directly without conforming to the funnelData shape.
 *
 * Should be called only once per upsell order (guard with localStorage in the caller).
 *
 * @param {Object} data - Upsell conversion input
 * @param {string} data.orderId      - Unique order identifier for this upsell
 * @param {string} data.email        - Customer email address
 * @param {number} data.saleAmount   - Final charged amount (after any discounts)
 * @param {string} data.productName  - Product name (e.g. "Sermorelin Injection")
 * @param {string} [data.productId]  - Product SKU / identifier
 * @param {string} [data.planLabel]  - Plan label appended to title (e.g. "3-Month Plan")
 * @param {string} [data.promoCode]  - Promo code applied (optional)
 * @param {number} [data.savings]    - Discount amount applied (optional)
 */
export const trackKatalysConversionUpsell = (data = {}) => {
  if (!window._revoffers_track) {
    logger.warn('⚠️ Katalys not initialized. Upsell conversion not tracked.')
    return
  }

  const orderId     = data.orderId || ''
  const email       = data.email   || ''
  const saleAmount  = data.saleAmount || 0
  const productName = data.productName || ''
  const productId   = data.productId   || ''
  const planLabel   = data.planLabel   || ''
  const promoCode   = data.promoCode   || ''
  const savings     = parseFloat(data.savings) || 0

  const lineItemTitle = planLabel ? `${productName} - ${planLabel}` : productName

  const conversionPayload = {
    action:             'convert',
    action_type:        'online-sale',
    order_id:           orderId,
    sale_amount:        saleAmount,
    subtotal_amount:    saleAmount,
    email_address:      email,
    order_status:       'pending',
    line_item_1_title:  lineItemTitle,
    line_item_1_sku:    productId,
    line_item_1_price:  saleAmount,
  }

  if (promoCode && savings > 0) {
    conversionPayload.discount_1_code   = promoCode
    conversionPayload.discount_1_amount = savings
  }

  logger.debug('📊 Katalys Upsell Conversion payload:', conversionPayload)

  try {
    window._revoffers_track.push(conversionPayload)
    logger.debug('✅ Katalys upsell conversion tracked successfully')
  } catch (error) {
    logger.error('❌ Error tracking Katalys upsell conversion:', error)
  }
}

export default {
  initializeKatalys,
  trackKatalysConversion,
  trackKatalysConversionUpsell,
}
